/**
 * Prayers page: morning prayers (Mon-Fri 8:00) + place for other prayer meetings.
 * Events from API by category "prayer". Grouped by type (Утренние молитвы / Другие встречи).
 */

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { ListSkeleton } from "../../components/ui/Skeleton.tsx";
import { EventCard } from "../../components/EventCard.tsx";
import {
  IconClock,
  IconMapPin,
  IconChevronRight,
} from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import {
  graphql,
  withMinDelay,
  EVENTS_QUERY,
  CATEGORY_BY_CODE_QUERY,
  type Event,
  type EventWithContent,
} from "../../lib/graphql.ts";
import {
  getDateKeyBangkok,
  getTodayDateKeyBangkok,
  formatDateShortBangkok,
  getDayOfMonthAndWeekdayBangkok,
  addDaysToDateKey,
} from "../../lib/bangkok-time.ts";
import { APP_TIMEZONE } from "../../config.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { sanitizeUrl } from "../../lib/url-utils.ts";

const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];

const MORNING_PRAYER_TITLE = "Утренние молитвы";

interface PrayerCategory {
  id: string;
  name: string;
  code: string;
  color: string | null;
}

function buildImageUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  if (url.startsWith("http")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

export function PrayersPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [category, setCategory] = useState<PrayerCategory | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  useEffect(() => {
    let cancelled = false;
    setCategory(null);
    setError(null);
    graphql<{ categoryByCode: PrayerCategory | null }>(CATEGORY_BY_CODE_QUERY, {
      code: "prayer",
    })
      .then((data) => {
        if (cancelled) return;
        setCategory(data.categoryByCode ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ref to track whether the component is mounted for async safety
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadEvents = useCallback(() => {
    if (!category?.id) return;
    setIsLoadingEvents(true);
    setError(null);
    const fromDate = getTodayDateKeyBangkok();
    const toDate = addDaysToDateKey(fromDate, 60);
    withMinDelay(
      graphql<{ events: Event[] }>(EVENTS_QUERY, {
        categoryIds: [category.id],
        timezone: APP_TIMEZONE,
        fromDate,
        toDate,
      })
    )
      .then((data) => {
        if (!isMountedRef.current) return;
        setEvents(data.events ?? []);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setError(err);
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setIsLoadingEvents(false);
      });
  }, [category?.id]);

  useEffect(() => {
    if (category?.id) loadEvents();
    else setEvents([]);
  }, [category?.id, loadEvents]);

  const { morningEvents, otherEvents } = useMemo(() => {
    const morning: Event[] = [];
    const other: Event[] = [];
    for (const e of events) {
      if (e.title === MORNING_PRAYER_TITLE) {
        morning.push(e);
      } else {
        other.push(e);
      }
    }
    morning.sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
    );
    other.sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
    );
    return { morningEvents: morning, otherEvents: other };
  }, [events]);

  const morningByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of morningEvents) {
      const dateKey = getDateKeyBangkok(event.startsAt);
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    return map;
  }, [morningEvents]);

  const morningDayKeys = useMemo(
    () => Array.from(morningByDay.keys()).sort(),
    [morningByDay]
  );

  if (error && !isLoadingEvents) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={category ? loadEvents : () => window.location.reload()}
        />
      </Layout>
    );
  }

  const mainMorning = morningEvents[0] as EventWithContent | undefined;
  const heroImageUrl =
    buildImageUrl(mainMorning?.imageUrl ?? null) ?? "/events/prayer.png";
  const descriptionStr = mainMorning?.description?.trim() ?? "";
  const venueStr = mainMorning?.venue ?? "";
  const hasMaps = Boolean(mainMorning?.mapsUrl);
  const todayDateKey = getTodayDateKeyBangkok();

  return (
    <Layout>
      <h1 className="text-xl font-bold text-slate-800 mb-4">
        Молитвенные встречи
      </h1>

      {/* Hero image */}
      {heroImageUrl ? (
        <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
          <img
            src={heroImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : null}

      {/* Section: Утренние молитвы */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-slate-800 mb-2">
          Утренние молитвы
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          ПН–ПТ 8:00–9:00 · Божья гора преображения
        </p>

        {descriptionStr ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 mb-4">
            <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap prose prose-slate prose-sm max-w-none">
              {descriptionStr}
            </p>
          </div>
        ) : null}

        {/* Когда и где */}
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 mb-4">
          <p className="text-slate-800 font-medium flex items-center gap-2 mb-2">
            <IconClock className="w-5 h-5 text-slate-500 flex-shrink-0" />
            ПН–ПТ 8:00–9:00
          </p>
          {venueStr ? (
            <p className="text-slate-700 flex items-center gap-2 mb-2">
              <IconMapPin className="w-5 h-5 text-slate-500 flex-shrink-0" />
              {venueStr}
            </p>
          ) : null}
          {hasMaps && mainMorning?.mapsUrl && sanitizeUrl(mainMorning.mapsUrl) ? (
            <a
              href={sanitizeUrl(mainMorning.mapsUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-accent)] no-underline"
            >
              Открыть карту
              <IconChevronRight className="w-4 h-4" />
            </a>
          ) : null}
        </div>

        {/* Ближайшие утренние молитвы */}
        <p className="section-title px-0 mb-3">Ближайшие встречи</p>
        {isLoadingEvents ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6">
            <ListSkeleton count={3} />
          </div>
        ) : morningDayKeys.length > 0 ? (
          <div className="space-y-6">
            {morningDayKeys.map((dateKey) => {
              const dayEvents = morningByDay.get(dateKey) ?? [];
              const { weekdayIndex } = getDayOfMonthAndWeekdayBangkok(dateKey);
              const dayName = WEEKDAYS_FULL[weekdayIndex];
              const dateStr = formatDateShortBangkok(dateKey);
              const isToday = dateKey === todayDateKey;

              return (
                <section key={dateKey}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {isToday ? "Сегодня" : dayName}
                      <span className="font-normal text-slate-400 ml-2">
                        {dateStr}
                      </span>
                    </span>
                  </div>
                  <div className="space-y-3">
                    {dayEvents.map((event) => (
                      <EventCard key={event.id} event={event} hideTitle />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm p-6 text-center border border-slate-100">
            <p className="text-slate-600 text-sm">
              Ближайшие утренние молитвы появятся в расписании.
            </p>
          </div>
        )}
      </section>

      {/* Section: Другие встречи (placeholder for future) */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-slate-800 mb-3">
          Другие встречи
        </h2>
        {otherEvents.length > 0 ? (
          <div className="space-y-3">
            {otherEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm p-6 text-center border border-slate-100">
            <p className="text-slate-500 text-sm">
              Скоро появятся новые форматы
            </p>
          </div>
        )}
      </section>

      {/* Link to full schedule */}
      <div className="mt-6">
        <Link
          to="/schedule"
          className="flex items-center justify-between gap-3 py-4 px-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
        >
          <span className="font-medium text-slate-800">
            Всё расписание на неделю
          </span>
          <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
        </Link>
      </div>

      <div className="h-4" />
    </Layout>
  );
}
